import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  App,
  Button,
  Block,
  Flex,
  FontoLogo,
  Form,
  Heading,
  Icon,
  Label,
  Masthead,
  MastheadContent,
  Statusbar,
  StatusbarAlignRight,
  TextArea,
  VerticalSeparationLine,
} from "fds/components";

function replaceEntitiesInHtml(htmlString) {
  if (!htmlString) {
    return "";
  }
  try {
    const dom = new window.DOMParser().parseFromString(htmlString, "text/html");
    // Parsing using the `.outerHTML` property forces entities to be revealed
    return dom.documentElement.outerHTML;
  } catch (error) {
    console.error(error);

    return "HTML could not be parsed.";
  }
}

export default function Clipboard() {
  const [inputValue, setInputValue] = useState("Paste here");
  const [htmlOutputValue, setHtmlOutputValue] = useState("");
  const [plainOutputValue, setPlainOutputValue] = useState("");
  const [xmlOutputValue, setXmlOutputValue] = useState("");
  const [rtfOutputValue, setRtfOutputValue] = useState("");
  const [applicationXmlOutputValue, setApplicationXmlOutputValue] =
    useState("");
  const [customLogOutput, setCustomLogOutput] = useState("");
  const [htmlOutputWithEntities, setHtmlOutputWithEntities] = useState("");
  const [selectedOutput, setSelectedOutput] = useState("");
  const [statusValue, setStatusValue] = useState("Content not pasted");
  const [isCopied, setIsCopied] = useState(false);

  const [valueByName, setValueByName] = useState({ textInput: "" });
  const handleFormFieldChange = useCallback(({ name, value }) => {
    setValueByName((prevValueByName) => ({
      ...prevValueByName,
      [name]: value,
    }));
  }, []);

  const inputRef = useRef(null);
  const handleInputRef = useCallback((node) => {
    inputRef.current = node;
  }, []);

  const isDisabled = !selectedOutput;

  const copyToClipboard = (content) => {
    navigator.clipboard.writeText(content);
    setIsCopied(true);
  };

  useEffect(() => {
    function handlePaste(event) {
      setPlainOutputValue(event.clipboardData.getData("text/plain"));
      setHtmlOutputValue(event.clipboardData.getData("text/html"));
      setXmlOutputValue(event.clipboardData.getData("text/xml"));
      setRtfOutputValue(event.clipboardData.getData("text/rtf"));
      setApplicationXmlOutputValue(
        event.clipboardData.getData("application/xml")
      );
      setCustomLogOutput(
        event.clipboardData.getData("application/vnd.fontoxml")
      );

      setHtmlOutputWithEntities(
        replaceEntitiesInHtml(event.clipboardData.getData("text/html"))
      );

      setInputValue("Select a mimetype to see the output");

      setIsCopied(false);
      setStatusValue(
        "Pasted: " +
          event.clipboardData.files.length +
          " files and " +
          event.clipboardData.types.length +
          " mimetypes"
      );
      event.preventDefault();
    }

    const inputRefCurrent = inputRef.current;
    inputRefCurrent.addEventListener("paste", handlePaste);

    return () => {
      inputRefCurrent.removeEventListener("paste", handlePaste);
    };
  }, [inputRef]);

  return (
    <App>
      <Masthead>
        <MastheadContent>
          <FontoLogo onClick={() => window.open("https://www.fontoxml.com")} />
        </MastheadContent>
      </Masthead>
      <Flex flexDirection="column" alignItems="center">
        <Flex flexDirection="column">
          <Flex applyCss={{ marginTop: "30px" }}>
            <Heading align="center" level="1">
              Welcome to the Fonto Clipboard Playground
            </Heading>
          </Flex>
          <Block
            applyCss={{
              margin: "60px auto",
            }}
          >
            <Icon
              icon="fal fa-copy"
              colorName="state-message-icon-muted-color"
              size="100px"
            />
          </Block>

          <Flex flexDirection="column" alignItems="center">
            <Form
              onFieldChange={handleFormFieldChange}
              valueByName={valueByName}
            >
              <Flex applyCss={{ width: "300px" }}>
                <TextArea
                  rows={{ minimum: 1, maximum: 1 }}
                  name="textInput"
                  onRef={handleInputRef}
                  value={inputValue}
                />
              </Flex>
            </Form>
          </Flex>
        </Flex>

        <Flex flexDirection="column" alignItems="center">
          <>
            <Flex paddingSize="m" applyCss={{ marginTop: "20px" }}>
              <Block applyCss={{ paddingLeft: "5px" }}>
                <Button
                  isBorderless
                  isDisabled={!htmlOutputValue}
                  label="text/html"
                  onClick={() => setSelectedOutput(htmlOutputValue)}
                />
              </Block>
              <Block applyCss={{ paddingLeft: "5px" }}>
                <Button
                  isBorderless
                  isDisabled={!plainOutputValue}
                  label="text/plain"
                  onClick={() => setSelectedOutput(plainOutputValue)}
                />
              </Block>
              <Block applyCss={{ paddingLeft: "5px" }}>
                <Button
                  isDisabled={!xmlOutputValue}
                  isBorderless
                  label="text/xml"
                  onClick={() => setSelectedOutput(xmlOutputValue)}
                />
              </Block>
              <Block applyCss={{ paddingLeft: "5px" }}>
                <Button
                  isDisabled={!rtfOutputValue}
                  isBorderless
                  label="text/rtf"
                  onClick={() => setSelectedOutput(rtfOutputValue)}
                />
              </Block>
              <Block applyCss={{ paddingLeft: "5px" }}>
                <Button
                  isDisabled={!applicationXmlOutputValue}
                  isBorderless
                  label="application/xml"
                  onClick={() => setSelectedOutput(applicationXmlOutputValue)}
                />
              </Block>
              <Block applyCss={{ paddingLeft: "5px" }}>
                <Button
                  isDisabled={!customLogOutput}
                  isBorderless
                  label="application/vnd.fontoxml"
                  onClick={() => setSelectedOutput(customLogOutput)}
                />
              </Block>
              <Block applyCss={{ paddingLeft: "5px" }}>
                <Button
                  isDisabled={!htmlOutputWithEntities}
                  isBorderless
                  label="text/html with added entities"
                  onClick={() => setSelectedOutput(htmlOutputWithEntities)}
                />
              </Block>
            </Flex>

            <Flex applyCss={{ marginTop: "30px" }}>
              <Button
                label="Copy"
                isDisabled={isDisabled}
                icon="fal fa-copy"
                onClick={() => copyToClipboard(selectedOutput)}
              />
            </Flex>
            <Flex
              paddingSize="m"
              applyCss={{
                width: "100%",
              }}
            >
              <TextArea
                isDisabled={isDisabled}
                rows={{ minimum: 15, maximum: 20 }}
                value={selectedOutput}
              />
            </Flex>
          </>
        </Flex>
      </Flex>
      <Flex
        applyCss={{
          height: "30px",
          position: "fixed",
          bottom: "0px",
          width: "100%",
        }}
      >
        <Statusbar>
          <StatusbarAlignRight>
            <Flex flexDirection="row">
              {isCopied && (
                <Flex applyCss={{ padding: "8px", flexDirection: "row" }}>
                  <Icon
                    icon="far fa-check"
                    size="20px"
                    colorName="state-message-success-color"
                  />
                  <Label isBold>Copied</Label>
                </Flex>
              )}
              <VerticalSeparationLine />
              <Block applyCss={{ padding: "8px" }}>
                <Label isBold>{statusValue}</Label>
              </Block>
            </Flex>
          </StatusbarAlignRight>
        </Statusbar>
      </Flex>
    </App>
  );
}
